// packages/api/src/hooks/queue/createQueueListingHook.ts
//
// Factory for config-driven queue listing hooks (L2.7). Every instance returns
// a `PaginatedResponse<TRow>`, normalizing the three divergent list shapes the
// backend emits — `{ items, total }`, `{ count, rows }`, `{ data, total }` — so
// no queue accidentally drops the total and gets stuck on page 1.
//
// The axios call + response unwrap stays a per-caller adapter (`fetchPage`);
// the factory owns key/normalize/staleTime + a standardized `invalidate`.

import { useQuery, type QueryClient, type UseQueryResult } from '@tanstack/react-query';

import type { PaginatedResponse } from '@alamo/types';

/** The raw list payloads any backend list endpoint may return (post axios unwrap). */
export type RawListPayload<T> =
  | { items: T[]; total?: number }
  | { count?: number; rows: T[] }
  | { data: T[]; total?: number }
  | T[];

/** Page-window inputs used to fill page/limit/totalPages when the payload omits them. */
export interface QueuePageParams {
  page?: number;
  limit?: number;
  pageSize?: number;
}

const DEFAULT_STALE_TIME = 5 * 60 * 1000;
const DEFAULT_LIMIT = 20;

/**
 * Collapse any of the three list shapes (or a bare array) into the canonical
 * `PaginatedResponse<T>`. Missing `total` falls back to the row count; page and
 * limit come from the caller's params (URL/query state).
 */
export function normalizeToPaginated<T>(
  raw: RawListPayload<T>,
  params?: QueuePageParams,
): PaginatedResponse<T> {
  let items: T[];
  let total: number;

  if (Array.isArray(raw)) {
    items = raw;
    total = raw.length;
  } else if ('rows' in raw && Array.isArray(raw.rows)) {
    items = raw.rows;
    total = typeof raw.count === 'number' ? raw.count : raw.rows.length;
  } else if ('items' in raw && Array.isArray(raw.items)) {
    items = raw.items;
    total = typeof raw.total === 'number' ? raw.total : raw.items.length;
  } else if ('data' in raw && Array.isArray(raw.data)) {
    items = raw.data;
    total = typeof raw.total === 'number' ? raw.total : raw.data.length;
  } else {
    items = [];
    total = 0;
  }

  const page = params?.page && params.page > 0 ? params.page : 1;
  const limit = params?.limit ?? params?.pageSize ?? (items.length || DEFAULT_LIMIT);
  const totalPages = limit > 0 ? Math.max(1, Math.ceil(total / limit)) : 1;

  return { items, total, page, limit, totalPages };
}

export interface QueueListingHookConfig<TRow, TFilters> {
  /** Root key for standardized invalidation (`.invalidate`). */
  baseQueryKey: readonly unknown[];
  /** Per-filters query key (usually `queryKeys.<resource>.list(filters)`). */
  queryKey: (filters?: TFilters) => readonly unknown[];
  /** Fetch + unwrap the raw list payload (any of the three shapes). */
  fetchPage: (filters?: TFilters) => Promise<RawListPayload<TRow>>;
  /** Derive page-window params from filters (defaults to reading page/limit off filters). */
  getPageParams?: (filters?: TFilters) => QueuePageParams;
  staleTime?: number;
}

/** A queue listing hook — callable + a standardized invalidator. */
export type QueueListingHook<TRow, TFilters> = {
  (filters?: TFilters): UseQueryResult<PaginatedResponse<TRow>>;
  baseQueryKey: readonly unknown[];
  invalidate: (queryClient: QueryClient) => Promise<void>;
};

export function createQueueListingHook<TRow, TFilters = Record<string, unknown>>(
  config: QueueListingHookConfig<TRow, TFilters>,
): QueueListingHook<TRow, TFilters> {
  function useQueueListing(filters?: TFilters): UseQueryResult<PaginatedResponse<TRow>> {
    return useQuery({
      queryKey: config.queryKey(filters),
      queryFn: async (): Promise<PaginatedResponse<TRow>> => {
        const raw = await config.fetchPage(filters);
        const params = config.getPageParams
          ? config.getPageParams(filters)
          : (filters as QueuePageParams | undefined);
        return normalizeToPaginated<TRow>(raw, params);
      },
      staleTime: config.staleTime ?? DEFAULT_STALE_TIME,
    });
  }

  return Object.assign(useQueueListing, {
    baseQueryKey: config.baseQueryKey,
    invalidate: (queryClient: QueryClient): Promise<void> =>
      queryClient.invalidateQueries({ queryKey: config.baseQueryKey }),
  });
}
